import type { MemberAggregate } from "@ext/enterprise/components/admin/settings/members/model/Member";
import { RepoAccessPickerDialog } from "@ext/enterprise/components/admin/settings/resources/dialog/RepoAccessPickerDialog";
import { EditorRevokeDialog } from "@ext/enterprise/components/admin/settings/users/dialog/EditorRevokeDialog";
import { AddButton } from "@ext/enterprise/components/admin/ui-kit/AddButton";
import { DeleteSelectedButton } from "@ext/enterprise/components/admin/ui-kit/DeleteSelectedButton";
import { SelectableTable } from "@ext/enterprise/components/admin/ui-kit/table/SelectableTable";
import t from "@ext/localization/locale/translate";
import { Divider } from "@ui-kit/Divider";
import { FieldLabel } from "@ui-kit/Label";
import { SwitchField } from "@ui-kit/Switch";
import type { ReactNode } from "react";
import { WithTooltip } from "../../../../../enterprise/components/admin/ui-kit/WithTooltip";
import type { MemberAccessFormState } from "./useMemberAccessForm";

interface MemberAccessFieldsProps {
	form: MemberAccessFormState;
	emailField: ReactNode;
	canManageAdmins: boolean;
	showAccesses: boolean;
}

export const MemberAccessFields = ({ form, emailField, canManageAdmins, showAccesses }: MemberAccessFieldsProps) => {
	const { data, access, editorSlotsFull, editorCount, effectiveEditorsUsed } = form;
	const isAdminEditorLocked = !canManageAdmins && data.isAdmin;
	const isEditorLocked = data.isOwner || isAdminEditorLocked || editorSlotsFull;

	return (
		<>
			<div className="flex flex-col gap-4">
				{emailField}

				<WithTooltip
					tooltip={
						data.isOwner
							? t("enterprise-cloud.org-settings.users.owner-always-editor-hint")
							: isAdminEditorLocked
								? t("enterprise-cloud.org-settings.users.admin.only-owner-can-change-admin-editor-hint")
								: editorSlotsFull
									? t("enterprise.admin.users.editor-slots-full")
									: undefined
					}
				>
					<SwitchField
						alignment="right"
						checked={data.isEditor}
						className="justify-between"
						description={
							editorCount > 0 ? (
								<>
									{t("enterprise.admin.users.editor-hint")} ({effectiveEditorsUsed}/{editorCount}{" "}
									{t("enterprise.admin.users.editor-slots-used")})
								</>
							) : undefined
						}
						disabled={isEditorLocked}
						label={t("enterprise.admin.users.editor")}
						onCheckedChange={isEditorLocked ? undefined : (c) => data.setIsEditor(Boolean(c))}
						outline
					/>
				</WithTooltip>

				<WithTooltip
					tooltip={
						data.isOwner
							? t("enterprise-cloud.org-settings.users.admin.owner-cant-be-admin-hint")
							: !canManageAdmins
								? t("enterprise-cloud.org-settings.users.admin.only-owner-can-change-hint")
								: !data.isEditor
									? t("enterprise-cloud.org-settings.users.admin.requires-editor-hint")
									: undefined
					}
				>
					<SwitchField
						alignment="right"
						checked={data.isAdmin}
						className="justify-between"
						description={t("enterprise-cloud.org-settings.users.admin.admin-hint")}
						disabled={data.isOwner || !canManageAdmins || !data.isEditor}
						label={t("enterprise-cloud.org-settings.users.roles.admin")}
						onCheckedChange={(c) => data.setIsAdmin(Boolean(c))}
						outline
					/>
				</WithTooltip>

				<WithTooltip
					tooltip={
						data.isOwner
							? t("enterprise-cloud.org-settings.users.ai-agent.owner-always-has-access-hint")
							: undefined
					}
				>
					<SwitchField
						alignment="right"
						checked={data.aiAgentAccess}
						className="justify-between"
						description={t("enterprise-cloud.org-settings.users.ai-agent.hint")}
						disabled={data.isOwner}
						label={t("enterprise-cloud.org-settings.users.ai-agent.access")}
						onCheckedChange={(c) => data.setAiAgentAccess(Boolean(c))}
						outline
					/>
				</WithTooltip>
			</div>

			{showAccesses && <MemberAccessTable access={access} />}
		</>
	);
};

const MemberAccessTable = ({ access }: { access: MemberAccessFormState["access"] }) => (
	<>
		<Divider />

		<section className="flex flex-col gap-6">
			<FieldLabel className="text-base">{t("enterprise.admin.accesses")}</FieldLabel>
			<SelectableTable
				columns={access.columns}
				data={access.rows}
				getRowId={access.getId}
				onRowSelectionChange={access.setSelection}
				rowSelection={access.selection}
				rowVersions={access.rowVersions}
				searchPlaceholder={t("enterprise.admin.resources.find")}
				toolbarActions={
					<>
						<DeleteSelectedButton count={access.selected.length} onClick={access.removeSelected} />
						<AddButton onClick={access.picker.open} />
					</>
				}
			/>
		</section>
	</>
);

interface MemberAccessDialogsProps {
	form: MemberAccessFormState;
	aggregate: MemberAggregate;
}

export const MemberAccessDialogs = ({ form, aggregate }: MemberAccessDialogsProps) => {
	const { access, roleRules, editorRevokeDialog } = form;

	return (
		<>
			<RepoAccessPickerDialog
				aggregate={aggregate}
				key={access.picker.key}
				onClose={access.picker.close}
				onPicked={access.picker.picked}
				open={access.picker.isOpen}
				preselected={access.preselected}
				roleRules={roleRules}
			/>

			<EditorRevokeDialog {...editorRevokeDialog} />
		</>
	);
};
